import type { ChatMessage } from '../types'
import type { ApprovalDecision } from './chat/approval-status'
import { approvalDecision } from './chat/approval-status'
import { TOKEN } from './tokens'

const PREVIEW_LIMIT = 48
const DURATION_OPTION = /duration/i
export const RESOLUTION_OPTION = /resolution/i
export const ASPECT_OPTION = /aspect.?ratio/i

type Approval = NonNullable<ChatMessage['approval']>
type MediaFacts = Pick<
  NonNullable<ChatMessage['media']>,
  'model' | 'options' | 'duration' | 'aspectRatio'
>

export interface PromptInputThumb {
  ref: string
  label: string
  previewUrl?: string
}

export interface PromptDetails {
  prompt?: string
  model?: string
  duration?: string
  resolution?: string
  aspectRatio?: string
  coinCost?: number
  settings?: string[]
  inputs: PromptInputThumb[]
}

export interface DecidedSummary {
  decision: ApprovalDecision
  model?: string
  promptPreview?: string
  coinCost?: number
  decidedAt?: number
}

export function knownModel(approval: Approval) {
  const id = approval.plan?.model.trim()
  if (!id) {
    return undefined
  }
  const label = approval.modelChoices?.find(choice => choice.model === id)?.label.trim()
  return label || id
}

export function knownCoinCost(approval: Approval) {
  if (approval.approvedCoinAmount !== undefined && Number.isFinite(approval.approvedCoinAmount)) {
    return approval.approvedCoinAmount
  }
  if (Number.isFinite(approval.coinCost) && approval.coinCost > 0) {
    return approval.coinCost
  }
  return undefined
}

export function promptPreview(prompt: string) {
  const flat = prompt.replace(/\s+/g, ' ').trim()
  if (!flat) {
    return undefined
  }
  if (flat.length <= PREVIEW_LIMIT) {
    return flat
  }
  const cut = flat.slice(0, PREVIEW_LIMIT - 1).trimEnd()
  if (!cut) {
    return undefined
  }
  return `${cut}…`
}

export function decidedSummary(approval: Approval): DecidedSummary | undefined {
  const decision = approvalDecision(approval)
  if (!decision) {
    return undefined
  }
  const model = knownModel(approval)
  const preview = promptPreview(approval.prompt)
  const coinCost = knownCoinCost(approval)
  return {
    decision,
    ...(model ? { model } : {}),
    ...(preview ? { promptPreview: preview } : {}),
    ...(coinCost !== undefined ? { coinCost } : {}),
    ...(approval.decidedAt ? { decidedAt: approval.decidedAt } : {}),
  }
}

export function promptDetailsFrom(input: {
  approval?: Approval
  media?: MediaFacts
}): PromptDetails {
  const ran = input.media?.options ?? []
  const proposed = input.approval?.plan?.options ?? []
  const prompt = input.approval?.prompt.trim()
  const model = mediaModel(input.media?.model, input.approval)
  const duration = durationText(ran, proposed, input.media?.duration)
  const resolution = setting(ran, proposed, RESOLUTION_OPTION)
  const aspectRatio = setting(ran, proposed, ASPECT_OPTION) ?? present(input.media?.aspectRatio)
  const coinCost = input.approval ? knownCoinCost(input.approval) : undefined
  const settings = detailSettings(ran, proposed, input.media)
  const inputs = (input.approval?.plan?.inputs ?? []).flatMap(item => {
    const ref = item.ref.trim()
    if (!ref) {
      return []
    }
    return [
      {
        ref,
        label: item.label || ref,
        ...(item.previewUrl ? { previewUrl: item.previewUrl } : {}),
      },
    ]
  })
  return {
    ...(prompt ? { prompt } : {}),
    ...(model ? { model } : {}),
    ...(duration ? { duration } : {}),
    ...(resolution ? { resolution } : {}),
    ...(aspectRatio ? { aspectRatio } : {}),
    ...(coinCost !== undefined ? { coinCost } : {}),
    ...(settings.length > 0 ? { settings } : {}),
    inputs,
  }
}

export function resultChips(
  plan: { model: string; options: readonly { name: string; value: string }[] } | undefined,
  modelLabel?: string,
  details?: Pick<PromptDetails, 'model' | 'resolution'>,
) {
  const model = details?.model || present(modelLabel) || present(plan?.model)
  const resolution = details
    ? details.resolution
    : optionValue(plan?.options ?? [], RESOLUTION_OPTION)
  return [model, resolution].flatMap(tag => (tag ? [tag] : []))
}

export function hasPromptDetails(details: PromptDetails) {
  return Boolean(
    details.prompt ||
    details.model ||
    details.duration ||
    details.resolution ||
    details.aspectRatio ||
    details.settings?.length ||
    details.coinCost !== undefined ||
    details.inputs.length > 0,
  )
}

export function rerunDraft(prompt: string, inputs: readonly { ref: string }[]) {
  const base = prompt.trim()
  const extras = inputs.flatMap(input => {
    const ref = input.ref.trim()
    if (!ref || base.includes(ref)) {
      return []
    }
    return [ref]
  })
  return [base, ...extras].filter(part => part.length > 0).join(' ')
}

function mediaModel(model: string | undefined, approval: Approval | undefined) {
  const fromResult = present(model)
  if (fromResult) {
    return fromResult
  }
  if (!approval) {
    return undefined
  }
  return knownModel(approval)
}

function setting(
  ran: readonly { name: string; value: string }[],
  proposed: readonly { name: string; value: string }[],
  pattern: RegExp,
) {
  return optionValue(ran, pattern) ?? optionValue(proposed, pattern)
}

export function optionValue(options: readonly { name: string; value: string }[], pattern: RegExp) {
  const match = options.find(option => pattern.test(option.name) && option.value.trim())
  return match?.value.trim()
}

function durationText(
  ran: readonly { name: string; value: string }[],
  proposed: readonly { name: string; value: string }[],
  seconds: number | undefined,
) {
  const value = setting(ran, proposed, DURATION_OPTION)
  if (value) {
    return withUnit(value)
  }
  if (!seconds || seconds <= 0) {
    return undefined
  }
  return `${seconds}s`
}

function withUnit(value: string) {
  return /^\d+(?:\.\d+)?$/.test(value) ? `${value}s` : value
}

function detailSettings(
  ran: readonly { name: string; value: string }[],
  proposed: readonly { name: string; value: string }[],
  media: { duration?: number; aspectRatio?: string } | undefined,
) {
  const values: string[] = []
  let sawDuration = false
  let sawAspect = false
  for (const option of mergeOptions(proposed, ran)) {
    if (RESOLUTION_OPTION.test(option.name)) {
      continue
    }
    const value = option.value.trim()
    if (DURATION_OPTION.test(option.name)) {
      sawDuration = true
      values.push(withUnit(value))
      continue
    }
    if (ASPECT_OPTION.test(option.name)) {
      sawAspect = true
    }
    values.push(value)
  }
  if (!sawDuration && media?.duration && media.duration > 0) {
    values.push(`${media.duration}s`)
  }
  if (!sawAspect) {
    const aspect = present(media?.aspectRatio)
    if (aspect) {
      values.push(aspect)
    }
  }
  return values
}

function mergeOptions(
  proposed: readonly { name: string; value: string }[],
  ran: readonly { name: string; value: string }[],
) {
  const byName = new Map<string, { name: string; value: string }>()
  const order: string[] = []
  for (const option of [...proposed, ...ran]) {
    const name = option.name.trim()
    const value = option.value.trim()
    if (!name || !value) {
      continue
    }
    const key = name.toLowerCase()
    if (!byName.has(key)) {
      order.push(key)
    }
    byName.set(key, option)
  }
  return order.flatMap(key => {
    const option = byName.get(key)
    return option ? [option] : []
  })
}

function present(value: string | undefined) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const REFERENCE_IMAGE = /\b(?:(?:the|a|this)\s+)?reference\s+image\b/gi

export type PromptRun = { kind: 'text'; text: string } | { kind: 'input'; index: number }

export function inlinePromptInputs(
  prompt: string,
  inputs: readonly { ref: string }[],
): PromptRun[] | undefined {
  if (!prompt || inputs.length === 0) {
    return undefined
  }
  const tokens = [...prompt.matchAll(new RegExp(TOKEN.source, 'g'))]
  if (tokens.length > 0) {
    const indexes = indexesForTokens(inputs, tokens)
    return indexes ? runsForMatches(prompt, tokens, indexes) : undefined
  }
  const phrases = [...prompt.matchAll(new RegExp(REFERENCE_IMAGE.source, 'gi'))]
  if (phrases.length === 0 || phrases.length !== inputs.length) {
    return undefined
  }
  return runsForMatches(
    prompt,
    phrases,
    phrases.map((_, index) => index),
  )
}

function indexesForTokens(inputs: readonly { ref: string }[], matches: RegExpMatchArray[]) {
  const byRef = new Map<string, number>()
  for (const [index, input] of inputs.entries()) {
    const ref = input.ref.trim()
    if (!ref || byRef.has(ref)) {
      return undefined
    }
    byRef.set(ref, index)
  }
  const indexes: number[] = []
  for (const match of matches) {
    const index = match[0] ? byRef.get(match[0]) : undefined
    if (index === undefined) {
      return undefined
    }
    indexes.push(index)
  }
  return indexes
}

function runsForMatches(prompt: string, matches: RegExpMatchArray[], indexes: readonly number[]) {
  const runs: PromptRun[] = []
  let cursor = 0
  for (const [position, match] of matches.entries()) {
    const token = match[0]
    const start = match.index
    const index = indexes[position]
    if (!token || start === undefined || index === undefined) {
      return undefined
    }
    if (start > cursor) {
      runs.push({ kind: 'text', text: prompt.slice(cursor, start) })
    }
    runs.push({ kind: 'input', index })
    cursor = start + token.length
  }
  if (cursor < prompt.length) {
    runs.push({ kind: 'text', text: prompt.slice(cursor) })
  }
  return runs
}
